import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";
import { SocialProviderIcon2 } from "src/app/pages/CreatorDiscoveryTool/legacy/app/components/SocialProviderIcon2/SocialProviderIcon2";
import OuterLink from "src/app/components/OuterLink/OuterLink";
import { Img } from "src/app/components/Img/Img";
import Pictogram, {
  PictogramVariants,
} from "src/app/components/Pictogram/Pictogram";
import { formatNumber, toFixedIfNecessary } from "src/utils/methods";
import { getRoute, ROUTES } from "src/utils/ROUTES";
import { GalleryListElement } from "src/app/components/Gallery/components/GalleryItem/GalleryItem";

import creatorPlaceholder from "src/images/creator-placeholder.svg";
import publicationPlaceholder from "src/images/eye-placeholder.svg";
import campaignPlaceholder from "src/images/project-placeholder.svg";

import "./HomepagePublicationCard.scss";
import {
  convertTikTokToWorkspaceVersion,
  SocialProvider,
} from "../../../../types";

interface Props {
  data: GalleryListElement;
  index: number;
  onThumbnailClick: (index: number) => void;
}

export function HomepagePublicationCard(props: Props) {
  const { data, index, onThumbnailClick } = props;

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const stats = {
    er: { value: data.engagementRate, type: "percent" },
    likes: { value: data.likes, type: "number" },
    views: { value: data.views, type: "number" },
    comments: { value: data.comments, type: "number" },
  };

  const renderStatValue = (value: number, type: string) => {
    if (type === "percent") {
      return `${toFixedIfNecessary((value ?? 0) * 100, 2)}%`;
    }

    return formatNumber(value);
  };

  return (
    <div className="gallery-item-wrapper" data-qa={data.url}>
      <div className="gallery-item">
        <div className="gallery-item__provider">
          <div className="gallery-item__provider-header">
            {data.campaignData ? (
              <Link
                className="homepage-publication-card__campaign"
                to={getRoute(ROUTES.PROJECT, {
                  wsWorkspaceUuid: activeWorkspaceUuid,
                  wsProjectUuid: data.campaignData.uuid,
                })}
              >
                <Img
                  src={data.campaignData.coverUrl ?? undefined}
                  fallbackImageSrc={campaignPlaceholder}
                  referrerPolicy="no-referrer"
                />
                <span className="gallery-item__provider-header-text">
                  {data.campaignData.name}
                </span>
              </Link>
            ) : (
              <span className="gallery-item__provider-header-text" />
            )}
          </div>

          <span className="gallery-item__provider-icon">
            {data.url?.length > 0 ? (
              <OuterLink
                className="gallery-item__provider-link"
                href={data.url}
              >
                <SocialProviderIcon2 provider={data.socialProvider} />
              </OuterLink>
            ) : (
              <SocialProviderIcon2 provider={data.socialProvider} />
            )}
          </span>
        </div>

        <div className="gallery-item__thumbnail">
          <div
            className="gallery-item__thumbnail-overlay"
            onClick={() => onThumbnailClick(index)}
          />
          <Img
            src={data.thumbUrl}
            fallbackImageSrc={publicationPlaceholder}
            referrerPolicy="no-referrer"
            alt="thumbnail"
            loading="lazy"
            allowHeicRecovery={
              convertTikTokToWorkspaceVersion(data.socialProvider) ===
              SocialProvider.TikTok
            }
          />
        </div>

        <div className="homepage-publication-card__creator">
          <Img
            src={data?.creatorData?.profilePictureUrl}
            fallbackImageSrc={creatorPlaceholder}
            referrerPolicy="no-referrer"
          />
          <span>{data?.creatorData?.username}</span>
        </div>

        <div className="gallery-item__data">
          {Object.entries(stats).map(([key, item]) => {
            if (!item.value) return null;

            return (
              <span className="gallery-item__data-item" key={key}>
                <Pictogram
                  identifier={`Social/${key}`}
                  variant={PictogramVariants.CoolGrey}
                />
                {renderStatValue(item.value, item.type)}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
